import type {
  Tenant,
  BitrixConnection,
  RegisteredBot,
  Flow,
  FlowNode,
  DashboardStats,
  PartnerClientSummary,
  TenantNotification,
} from '../types';
import { isPartnerReadOnlyMode } from './readOnlyMode';


export interface FlowImportSummary {
  name: string;
  description?: string;
  nodeCount: number;
  edgeCount: number;
  droppedEdges: number;
  executionMode: 'conventional' | 'continuous';
  issues: Array<{ level: 'error' | 'warn'; message: string }>;
}

export interface PlanChangeQuote {
  mode: 'upgrade' | 'downgrade' | 'unchanged' | 'no_subscription';
  subscriptionId: string | null;
  currency: string;
  currentBots: number;
  nextBots: number;
  /** Cobrado no cartão agora (só em upgrade), em centavos. */
  immediateChargeCents: number;
  /** Valor recorrente a partir da próxima renovação, em centavos. */
  nextCycleCents: number;
  effectiveAt: 'now' | 'period_end' | null;
  nextRenewalAt: string | null;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async refreshTenantToken(): Promise<string | null> {
    const tenantId = localStorage.getItem('tenantId');
    if (!tenantId) return null;
    try {
      const response = await fetch(`${this.baseUrl}/bitrix/oauth/get-tenant-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId }),
      });
      if (!response.ok) return null;
      const data = await response.json();
      if (data?.tenantToken) {
        localStorage.setItem('auth_token', data.tenantToken);
        return data.tenantToken;
      }
    } catch {
      // ignore
    }
    return null;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    _isRetry = false,
  ): Promise<T> {
    const method = (options.method || 'GET').toUpperCase();

    if (isPartnerReadOnlyMode() && method !== 'GET') {
      throw new Error('Modo leitura ativo: alterações no tenant do cliente não são permitidas.');
    }

    const url = `${this.baseUrl}${endpoint}`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Add auth token if available (from Bitrix24 context or local storage)
    const token = localStorage.getItem('auth_token');
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // On 401: try to refresh token using tenantId from localStorage, then retry once
    if (response.status === 401 && !_isRetry && method !== 'GET') {
      const newToken = await this.refreshTenantToken();
      if (newToken) {
        return this.request<T>(endpoint, options, true);
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const err = new Error(
        error.error_description ||
          error.error ||
          error.message ||
          `HTTP error ${response.status}`,
      );
      // Preserva o detalhe do backend para o chamador mostrar (ex.: erros de validação).
      (err as any).status = response.status;
      (err as any).code = error.error;
      (err as any).errors = error.errors;
      (err as any).details = error;
      throw err;
    }

    if (response.status === 204) {
      return {} as T;
    }

    try {
      const text = await response.text();
      return text ? JSON.parse(text) : ({} as T);
    } catch {
       return {} as T;
    }
  }

  private async requestWithHeaders<T>(
    endpoint: string,
    customHeaders: Record<string, string>,
    options: RequestInit = {},
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      headers: {
        ...(options.headers || {}),
        ...customHeaders,
      },
    });
  }

  // ============ Tenant ============
  
  // Public method for custom API requests
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint);
  }

  async getTenantByDomain(domain: string): Promise<Tenant> {
    return this.request<Tenant>(`/tenants/by-domain?domain=${encodeURIComponent(domain)}`);
  }

  async getTenant(tenantId: string): Promise<Tenant> {
    return this.request<Tenant>(`/tenants/${tenantId}`);
  }

  async updateTenant(tenantId: string, data: Partial<Tenant>): Promise<Tenant> {
    return this.request<Tenant>(`/tenants/${tenantId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async sendTestNotification(tenantId: string): Promise<void> {
    await this.request(`/tenants/${tenantId}/test-notification`, {
      method: 'POST',
    });
  }

  // ============ Connections ============
  async getConnections(tenantId: string): Promise<BitrixConnection[]> {
    return this.request<BitrixConnection[]>(`/tenants/${tenantId}/connections`);
  }

  async getConnection(connectionId: string): Promise<BitrixConnection> {
    return this.request<BitrixConnection>(`/bitrix/connection/${connectionId}`);
  }

  async getBitrixFields(connectionId: string): Promise<any> {
    return this.request<any>(`/bitrix/connection/${connectionId}/fields`);
  }

  async reconnectConnection(connectionId: string): Promise<BitrixConnection> {
    const response = await this.request<{ connection?: BitrixConnection }>(`/bitrix/connections/${connectionId}/repair`, {
      method: 'POST',
    });

    if (response?.connection) {
      return response.connection;
    }

    return this.request<BitrixConnection>(`/bitrix/connection/${connectionId}`);
  }

  async syncBitrixAppAuth(data: {
    domain?: string;
    member_id?: string;
    memberId?: string;
    auth: {
      access_token: string;
      refresh_token?: string;
      expires_in?: number | string;
      domain?: string;
      member_id?: string;
      user_id?: string | number;
      scope?: string;
    };
  }): Promise<{ success: boolean; updated: boolean; connectionId?: string; tenantToken?: string; reason?: string }> {
    // OAuth sync must never be blocked by partner read-only mode, because it is
    // an auth-healing operation (no tenant content mutation).
    const url = `${this.baseUrl}/bitrix/oauth/sync-app-auth`;
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    const token = localStorage.getItem('auth_token');
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `HTTP error ${response.status}`);
    }

    return response.json().catch(() => ({ success: false, updated: false }));
  }

  async deleteConnection(connectionId: string): Promise<void> {
    return this.request<void>(`/connections/${connectionId}`, {
      method: 'DELETE',
    });
  }

  // ============ Bots ============
  async getBots(tenantId: string): Promise<RegisteredBot[]> {
    return this.request<RegisteredBot[]>(`/tenants/${tenantId}/bots`);
  }

  async createBot(
    connectionId: string,
    data: { name: string; code: string; description?: string; openLineIds?: number[] }
  ): Promise<RegisteredBot> {
    return this.request<RegisteredBot>(`/bitrix/connection/${connectionId}/bots`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getOpenLines(connectionId: string): Promise<Array<{ id: number; name: string; botId?: number }>> {
    return this.request<Array<{ id: number; name: string; botId?: number }>>(`/bitrix/connection/${connectionId}/openlines`);
  }

  async updateBot(
    botId: string,
    data: Partial<RegisteredBot>
  ): Promise<RegisteredBot> {
    return this.request<RegisteredBot>(`/bitrix/bots/${botId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteBot(botId: string): Promise<void> {
    return this.request<void>(`/bitrix/bots/${botId}`, {
      method: 'DELETE',
    });
  }

  async assignFlowToBot(botId: string, flowId: string): Promise<RegisteredBot> {
    return this.request<RegisteredBot>(`/bitrix/bots/${botId}/flow`, {
      method: 'PUT',
      body: JSON.stringify({ flowId }),
    });
  }

  async unassignFlowFromBot(botId: string): Promise<RegisteredBot> {
    return this.request<RegisteredBot>(`/bitrix/bots/${botId}/flow`, {
      method: 'DELETE',
    });
  }

  async toggleBot(botId: string, active: boolean): Promise<RegisteredBot> {
    return this.request<RegisteredBot>(`/bitrix/bots/${botId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ active }),
    });
  }

  // ============ Flows ============
  async getFlows(tenantId: string): Promise<Flow[]> {
    return this.request<Flow[]>(`/flows?tenantId=${tenantId}`);
  }

  async getFlow(flowId: string): Promise<Flow> {
    return this.request<Flow>(`/flows/${flowId}`);
  }

  async createFlow(
    tenantId: string,
    data: { name: string; description?: string; connectionId?: string }
  ): Promise<Flow> {
    return this.request<Flow>(`/flows`, {
      method: 'POST',
      body: JSON.stringify({ ...data, tenantId }),
    });
  }

  async updateFlow(flowId: string, data: Partial<Flow>): Promise<Flow> {
    return this.request<Flow>(`/flows/${flowId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async publishFlow(flowId: string): Promise<Flow> {
    return this.request<Flow>(`/flows/${flowId}/publish`, {
      method: 'POST',
    });
  }

  async archiveFlow(flowId: string): Promise<Flow> {
    return this.request<Flow>(`/flows/${flowId}/archive`, {
      method: 'POST',
    });
  }

  async duplicateFlow(flowId: string, name?: string): Promise<Flow> {
    return this.request<Flow>(`/flows/${flowId}/duplicate`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async deleteFlow(flowId: string): Promise<void> {
    return this.request<void>(`/flows/${flowId}`, {
      method: 'DELETE',
    });
  }

  // ============ Flow Nodes ============
  async getFlowNodes(flowId: string): Promise<FlowNode[]> {
    return this.request<FlowNode[]>(`/flows/${flowId}/nodes`);
  }

  async createFlowNode(flowId: string, data: Partial<FlowNode>): Promise<FlowNode> {
    return this.request<FlowNode>(`/flows/${flowId}/nodes`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateFlowNode(nodeId: string, data: Partial<FlowNode>): Promise<FlowNode> {
    return this.request<FlowNode>(`/nodes/${nodeId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteFlowNode(nodeId: string): Promise<void> {
    return this.request<void>(`/nodes/${nodeId}`, {
      method: 'DELETE',
    });
  }

  // ============ Dashboard ============
  async getDashboardStats(tenantId: string): Promise<DashboardStats> {
    return this.request<DashboardStats>(`/tenants/${tenantId}/stats`);
  }

  // ============ Settings ============
  async updateSettings(
    tenantId: string,
    settings: Record<string, any>
  ): Promise<void> {
    return this.request<void>(`/tenants/${tenantId}/settings`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async saveOpenAIKey(tenantId: string, apiKey: string): Promise<void> {
    return this.request<void>(`/tenants/${tenantId}/openai-key`, {
      method: 'PUT',
      body: JSON.stringify({ apiKey }),
    });
  }

  async getOpenAIKeyStatus(tenantId: string): Promise<{ configured: boolean; maskedKey?: string }> {
    return this.request<{ configured: boolean; maskedKey?: string }>(`/tenants/${tenantId}/openai-key/status`);
  }

  async removeOpenAIKey(tenantId: string): Promise<void> {
    return this.request<void>(`/tenants/${tenantId}/openai-key`, {
      method: 'DELETE',
    });
  }

  // ─── Gemini Key ─────────────────────────────────────────────────────

  async saveGeminiKey(tenantId: string, apiKey: string): Promise<void> {
    return this.request<void>(`/tenants/${tenantId}/gemini-key`, {
      method: 'PUT',
      body: JSON.stringify({ apiKey }),
    });
  }

  async getGeminiKeyStatus(tenantId: string): Promise<{ configured: boolean; maskedKey?: string }> {
    return this.request<{ configured: boolean; maskedKey?: string }>(`/tenants/${tenantId}/gemini-key/status`);
  }

  async removeGeminiKey(tenantId: string): Promise<void> {
    return this.request<void>(`/tenants/${tenantId}/gemini-key`, {
      method: 'DELETE',
    });
  }

  // ─── Claude (Anthropic) Key ─────────────────────────────────────────

  async saveAnthropicKey(tenantId: string, apiKey: string): Promise<void> {
    return this.request<void>(`/tenants/${tenantId}/anthropic-key`, {
      method: 'PUT',
      body: JSON.stringify({ apiKey }),
    });
  }

  async getAnthropicKeyStatus(tenantId: string): Promise<{ configured: boolean; maskedKey?: string }> {
    return this.request<{ configured: boolean; maskedKey?: string }>(`/tenants/${tenantId}/anthropic-key/status`);
  }

  async removeAnthropicKey(tenantId: string): Promise<void> {
    return this.request<void>(`/tenants/${tenantId}/anthropic-key`, {
      method: 'DELETE',
    });
  }

  async getPartnerClients(partnerTenantId: string): Promise<PartnerClientSummary[]> {
    return this.request<PartnerClientSummary[]>(`/tenants/${partnerTenantId}/partner-clients`);
  }

  async linkPartnerClientByDomain(
    partnerTenantId: string,
    portalDomain: string,
  ): Promise<{ success: boolean; status: string; message: string; notificationId?: string; clientTenantId?: string }> {
    return this.request(`/tenants/${partnerTenantId}/partner-clients/link-by-domain`, {
      method: 'POST',
      body: JSON.stringify({ portalDomain }),
    });
  }

  async unlinkPartnerClient(partnerTenantId: string, clientTenantId: string): Promise<void> {
    return this.request<void>(`/tenants/${partnerTenantId}/partner-clients/${clientTenantId}`, {
      method: 'DELETE',
    });
  }

  async renewPartnerClient(partnerTenantId: string, clientTenantId: string): Promise<Tenant> {
    return this.request<Tenant>(`/tenants/${partnerTenantId}/partner-clients/${clientTenantId}/renew`, {
      method: 'POST',
    });
  }

  async setPartnerClientPlan(
    partnerTenantId: string,
    clientTenantId: string,
    botLimit: number,
    status: 'active' | 'suspended',
  ): Promise<void> {
    return this.request<void>(`/tenants/${partnerTenantId}/partner-clients/${clientTenantId}/plan`, {
      method: 'PUT',
      body: JSON.stringify({ botLimit, status }),
    });
  }

  async updatePartnerClientContactEmail(
    partnerTenantId: string,
    clientTenantId: string,
    technicalResponsibleEmail: string,
  ): Promise<Tenant> {
    return this.request<Tenant>(`/tenants/${partnerTenantId}/partner-clients/${clientTenantId}/contact-email`, {
      method: 'PUT',
      body: JSON.stringify({ technicalResponsibleEmail }),
    });
  }

  async getTenantNotifications(tenantId: string): Promise<TenantNotification[]> {
    return this.request<TenantNotification[]>(`/tenants/${tenantId}/notifications`);
  }

  async acceptTenantNotification(tenantId: string, notificationId: string): Promise<TenantNotification> {
    return this.request<TenantNotification>(`/tenants/${tenantId}/notifications/${notificationId}/accept`, {
      method: 'POST',
    });
  }

  async rejectTenantNotification(tenantId: string, notificationId: string): Promise<TenantNotification> {
    return this.request<TenantNotification>(`/tenants/${tenantId}/notifications/${notificationId}/reject`, {
      method: 'POST',
    });
  }

  async activatePartner(tenantId: string, partnerCode: string): Promise<Tenant> {
    return this.request<Tenant>(`/tenants/${tenantId}/partner/activate`, {
      method: 'POST',
      body: JSON.stringify({ partnerCode }),
    });
  }

  // ============ Billing (Stripe) ============
  async getBillingConfig(): Promise<{
    publishableKey: string;
    plans: Array<{
      code: string;
      name: string;
      bots: number;
      monthlyPriceUsd: number;
      stripePriceId: string;
      recommended: boolean;
      isAddon: boolean;
    }>;
  }> {
    return this.request('/billing/config');
  }

  async createStripeCheckoutSession(
    tenantId: string,
    priceId: string,
    couponCode?: string,
    targetTenantId?: string,
  ): Promise<{ url: string }> {
    return this.request('/billing/checkout-session', {
      method: 'POST',
      body: JSON.stringify({ tenantId, priceId, couponCode, targetTenantId }),
    });
  }

  /** Signed export built by the server — the signature is what import checks. */
  async exportFlow(flowId: string): Promise<Record<string, any>> {
    return this.request(`/flows/${flowId}/export`);
  }

  /** Dry run: the server validates the file and describes it. Writes nothing. */
  async previewFlowImport(payload: unknown): Promise<FlowImportSummary> {
    return this.request('/flows/import/preview', {
      method: 'POST',
      body: JSON.stringify({ payload }),
    });
  }

  async importFlow(payload: unknown, name?: string): Promise<{ flow: Flow; summary: FlowImportSummary }> {
    return this.request('/flows/import', {
      method: 'POST',
      body: JSON.stringify({ payload, name }),
    });
  }

  async previewPlanChange(
    tenantId: string,
    priceId: string,
    quantity = 1,
    targetTenantId?: string,
  ): Promise<PlanChangeQuote> {
    return this.request('/billing/preview-plan-change', {
      method: 'POST',
      body: JSON.stringify({ tenantId, priceId, quantity, targetTenantId }),
    });
  }

  async changePlan(
    tenantId: string,
    priceId: string,
    quantity = 1,
    targetTenantId?: string,
  ): Promise<PlanChangeQuote & { applied: boolean }> {
    return this.request('/billing/change-plan', {
      method: 'POST',
      body: JSON.stringify({ tenantId, priceId, quantity, targetTenantId }),
    });
  }

  async createStripePortalSession(tenantId: string): Promise<{ url: string }> {
    return this.request('/billing/portal-session', {
      method: 'POST',
      body: JSON.stringify({ tenantId }),
    });
  }

  async createStripePortalSessionForTarget(tenantId: string, targetTenantId: string): Promise<{ url: string }> {
    return this.request('/billing/portal-session', {
      method: 'POST',
      body: JSON.stringify({ tenantId, targetTenantId }),
    });
  }

  async syncStripeBillingStatus(tenantId: string): Promise<Tenant> {
    return this.request('/billing/sync-status', {
      method: 'POST',
      body: JSON.stringify({ tenantId }),
    });
  }

  // ============ Admin ============
  async adminLogin(username: string, password: string): Promise<{ token: string; expiresAt: string }> {
    return this.request('/admin/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  }

  async getAdminTenants(authHeader: string): Promise<any[]> {
    return this.requestWithHeaders('/admin/tenants', { Authorization: authHeader }, {
      method: 'GET',
    });
  }

  async generatePartnerKeyForTenant(authHeader: string, tenantId: string): Promise<{ tenantId: string; partnerKey: string }> {
    return this.requestWithHeaders(`/admin/tenants/${tenantId}/generate-partner-key`, { Authorization: authHeader }, {
      method: 'POST',
    });
  }

  async adminNotifyTenant(
    authHeader: string,
    tenantId: string,
    title: string,
    message: string,
  ): Promise<{ success: true; notificationId: string }> {
    return this.requestWithHeaders(`/admin/tenants/${tenantId}/notify`, { Authorization: authHeader }, {
      method: 'POST',
      body: JSON.stringify({ title, message }),
    });
  }

  async adminUnlinkPartner(
    authHeader: string,
    tenantId: string,
  ): Promise<{ success: true; tenantId: string; unlinkedFromPartner: boolean; detachedClientsCount: number }> {
    return this.requestWithHeaders(`/admin/tenants/${tenantId}/unlink-partner`, { Authorization: authHeader }, {
      method: 'POST',
    });
  }

  async adminAddFreeDays(
    authHeader: string,
    tenantId: string,
    days: number,
  ): Promise<{ success: true; tenantId: string; daysAdded: number; status: string; trialEndsAt: string | null }> {
    return this.requestWithHeaders(`/admin/tenants/${tenantId}/add-free-days`, { Authorization: authHeader }, {
      method: 'POST',
      body: JSON.stringify({ days }),
    });
  }
}

// Export singleton instance
export const api = new ApiClient(API_BASE_URL);

// Also export class for testing
export { ApiClient };
